import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bot, Copy, Download, Link as LinkIcon, Save } from "lucide-react";
import { downloadZipAuthed } from "./helpers";

export function DiscordImportCard({ onDone }) {
  const [f, setF] = useState({ bot_token: "", guild_id: "", default_role: "employee", email_domain: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const loadSettings = () => api.get("/discord/settings").then(({ data }) => {
    setSettings(data);
    setF((s) => ({
      ...s,
      guild_id: data.guild_id || "",
      default_role: data.default_role || "employee",
      email_domain: data.email_domain || "",
    }));
  }).catch(() => {});

  useEffect(() => {
    api.get("/discord/webhook-info").then(({ data }) => setWebhookInfo(data)).catch(() => {});
    loadSettings();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/discord/import-members", f);
      setResult(data);
      toast.success(`${data.created} imported · ${data.updated} updated`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Discord import failed");
    } finally {
      setBusy(false);
    }
  };

  const persist = async (patch) => {
    setSavingSettings(true);
    try {
      const payload = { guild_id: f.guild_id, default_role: f.default_role, email_domain: f.email_domain, ...patch };
      if (f.bot_token) payload.bot_token = f.bot_token;
      const { data } = await api.post("/discord/settings", payload);
      setSettings(data);
      toast.success(patch.auto_sync_enabled === undefined
        ? "Settings saved"
        : (patch.auto_sync_enabled ? "Daily sync enabled" : "Daily sync paused"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSavingSettings(false);
    }
  };

  const inputCls = "bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)] text-sm w-full";

  return (
    <div className="card-surface admin-card p-5" data-testid="discord-import-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Bot size={18} className="text-[color:var(--accent)]" />
          <h3 className="text-lg font-bold">Discord sync</h3>
        </div>
        <button
          type="button"
          onClick={() => downloadZipAuthed("/discord/bot-starter.zip", "nexuspulse-bot-starter.zip")}
          data-testid="dc-bot-starter"
          className="text-xs px-3 py-1.5 rounded-lg border border-slate-700 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] inline-flex items-center gap-1.5 text-slate-300"
        >
          <Download size={12} /> Bot starter kit
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Paste your Discord bot token and server (guild) ID to pull every member in. The bot must be in the server and have the <strong>Server Members Intent</strong> enabled.
      </p>
      <form onSubmit={submit} className="grid grid-cols-1 gap-2">
        <input
          value={f.bot_token}
          onChange={(e) => setF({ ...f, bot_token: e.target.value })}
          placeholder={settings?.has_bot_token ? "Bot token saved — paste a new one to replace" : "Bot token"}
          type="password"
          data-testid="dc-bot-token"
          className={inputCls}
        />
        <div className="grid grid-cols-2 gap-2">
          <input value={f.guild_id} onChange={(e) => setF({ ...f, guild_id: e.target.value })} placeholder="Server (guild) ID" required data-testid="dc-guild-id" className={inputCls} />
          <select value={f.default_role} onChange={(e) => setF({ ...f, default_role: e.target.value })} data-testid="dc-default-role" className={inputCls}>
            <option value="employee">Import as Employee</option>
            <option value="manager">Import as Manager</option>
            <option value="admin">Import as Admin</option>
          </select>
        </div>
        <input value={f.email_domain} onChange={(e) => setF({ ...f, email_domain: e.target.value })} placeholder="Email domain (optional, e.g. yourcompany.com)" data-testid="dc-email-domain" className={inputCls} />
        <div className="flex gap-2">
          <button disabled={busy} data-testid="dc-import" className="flex-1 px-3 py-2 rounded-lg bg-[color:var(--accent)] text-black font-bold text-sm disabled:opacity-60">
            {busy ? "Pulling…" : "Pull members now"}
          </button>
          <button
            type="button"
            onClick={() => persist({})}
            disabled={savingSettings}
            data-testid="dc-save-settings"
            className="px-3 py-2 rounded-lg border border-slate-700 hover:border-[color:var(--accent)] text-slate-300 text-sm font-bold inline-flex items-center gap-1"
          >
            <Save size={12} /> Save
          </button>
        </div>
      </form>
      {result && (
        <div className="mt-3 text-xs text-slate-300 space-y-1" data-testid="discord-result">
          ✓ Created: <span className="font-bold text-[color:var(--accent)]">{result.created}</span> · Updated: {result.updated} · Fetched: {result.total_fetched}
        </div>
      )}

      {settings && (
        <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between gap-3" data-testid="auto-sync-row">
          <div>
            <div className="text-sm font-semibold text-slate-200">Daily auto-sync</div>
            <div className="text-xs text-slate-500">
              Runs at 06:00 UTC. {settings.auto_sync_enabled ? "Enabled" : "Paused"}
              {settings.last_sync_at && <> · last run {new Date(settings.last_sync_at).toLocaleString()}</>}
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="dc-auto-sync"
              checked={settings.auto_sync_enabled}
              onChange={(e) => persist({ auto_sync_enabled: e.target.checked })}
              className="accent-[color:var(--accent)] w-4 h-4"
            />
            <span className="text-xs text-slate-300">{settings.auto_sync_enabled ? "On" : "Off"}</span>
          </label>
        </div>
      )}

      {webhookInfo && (
        <div className="mt-5 pt-4 border-t border-slate-800">
          <div className="flex items-center gap-2 mb-2">
            <LinkIcon size={14} className="text-slate-400" />
            <span className="text-xs uppercase tracking-widest text-slate-400">Real-time events webhook</span>
          </div>
          <p className="text-xs text-slate-500 mb-2">
            Have your Discord bot POST to this URL every time someone joins, leaves, sends a message, spends voice time or bumps the server — the metrics dashboard picks it up automatically.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-[11px] break-all bg-[#0b0d12] border border-[#2a3447] rounded px-2 py-1.5 text-slate-300" data-testid="discord-webhook-url">{webhookInfo.url}</code>
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(webhookInfo.url); toast.success("Copied"); }}
              className="text-slate-400 hover:text-[color:var(--accent)]"
              title="Copy"
            >
              <Copy size={14} />
            </button>
          </div>
          <div className="text-[11px] text-slate-500 mt-2">Types: <span className="text-slate-300">join, leave, message, voice_hours, bump</span></div>
        </div>
      )}
    </div>
  );
}

export default DiscordImportCard;
